import React, { useState, useEffect, useContext } from 'react';
import { Paper, Box, IconButton } from '@mui/material';
import { AlertContext } from "../App/App.js";
import { getQueryParamValue, isMobile, parseJSON } from "../App/utils";
import CloseIcon from '@mui/icons-material/Close';
import { CodeViewer } from "./CodeViewer";
import { ResizableBox } from 'react-resizable';
import 'react-resizable/css/styles.css';

export const CodeEditorConsole = ({ codeLoading, response, text, onClose, isFullscreen, mobileMenu }) => {
    const MAX_DISPLAY_LENGTH = 500000; // Maximum characters to display
    const [imageSrc, setImageSrc] = useState('');
    const [height, setHeight] = useState(getQueryParamValue('fullscreen') ? 140 : 100);
    const Alert = useContext(AlertContext);
    const leftMenuWidth = isMobile ? (mobileMenu ? '32vh' : 0) : '32vh';

    useEffect(() => {
        if (codeLoading) setImageSrc('');
    }, [codeLoading]);

    useEffect(() => {
        if (imageSrc && height < 512) setHeight(512);
    }, [imageSrc, height]);

    useEffect(() => {
        if (response && typeof response === 'string') {
            try {
                const parsedResponse = parseJSON(response)?.response;
                if (parsedResponse?.ContentType && parsedResponse?.base64Data) {
                    setImageSrc(`data:${parsedResponse.ContentType};base64,${parsedResponse.base64Data}`);
                }
            } catch (e) {
                console.error('Error parsing JSON:', e);
            }
        } else {
            setImageSrc('');
        }
    }, [response]);

    const shortResponse = response && response.length > MAX_DISPLAY_LENGTH
        ? `${response.substring(0, MAX_DISPLAY_LENGTH)}...`
        : response;

    const responseData = parseJSON(response);

    const finalResponse = shortResponse !== response
        ? shortResponse
        : (responseData ? JSON.stringify(responseData, null, 2) : response);

    const handleResize = (event, { size }) => {
        setHeight(size.height);
    };

    const customHandleStyle = {
        width: '100%',
        height: isMobile ? '6px' : '4px',
        background: 'rgba(255, 255, 255, 0.1)',
        cursor: 'ns-resize',
        position: 'absolute',
        top: '0',
        left: '0',
        padding: '2px 0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 0 1px rgba(0, 0, 0, 0.5)',
    };

    return (
        <ResizableBox
            width={`calc(100% - ${leftMenuWidth})`}
            height={height}
            minConstraints={[Infinity, 100]}
            maxConstraints={[Infinity, 600]}
            axis="y"
            handle={<span style={customHandleStyle} />}
            handleSize={[8, 8]}
            resizeHandles={['n']}
            onResize={handleResize}
            style={{
                position: 'fixed',
                bottom: 0,
                left: leftMenuWidth,
                right: 0,
                zIndex: 9999,
            }}
        >
            <Paper sx={{
                overflow: 'auto',
                color: 'text.primary',
                padding: 0,
                backgroundColor: 'background.paper',
                whiteSpace: 'pre-wrap',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}>
                {imageSrc && (
                    <Box sx={{ mt: 2, position: 'relative' }}>
                        <IconButton
                            onClick={onClose}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                color: 'primary.main', // Adjust color as needed
                                zIndex: 1, // Ensure the button is above other content
                            }}
                        >
                            <CloseIcon style={{ fontSize: '1rem' }} />
                        </IconButton>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <img width={512} src={imageSrc} alt="Embedded" style={{ maxWidth: '100%', maxHeight: '100vh' }} />
                        </Box>
                    </Box>
                )}
                {!imageSrc && (codeLoading || response) && (
                    <Box sx={{ flex: 1, position: 'relative', marginTop:'8px' }}>
                        <CodeViewer
                            styleCopy={{
                                position: isFullscreen ? 'fixed' : 'relative',
                                right: codeLoading ? '0px' : '40px', // Adjust position if noCopy is true
                                zIndex: 10,
                                color: 'rgba(0, 0, 0, 0.54)',
                            }}
                            styleClose={{
                                position: isFullscreen ? 'fixed' : 'relative',
                                right: '0px',
                                zIndex: 10,
                                color: 'rgba(0, 0, 0, 0.54)',
                            }}
                            isFullscreen={isFullscreen}
                            noCopy={true}
                            onClose={!codeLoading && onClose}
                            style={{ width: '100%', backgroundColor: '#181717', height: '100%' }}
                            setSystemAlert={Alert.setSystemAlert}
                            source={!!codeLoading ? text : finalResponse}
                            limitedWidth={true}
                            language={"json"}
                            className="codeWrapperAPI"
                        />
                    </Box>
                )}
            </Paper>
        </ResizableBox>
    );
};